import { useState, useEffect, useRef, useCallback } from "react";
import { Markdown } from "zudoku/components";

interface SDKTab {
  id: string;
  label: string;
  /** Multiple sources are merged by version (fixed-versioning monorepos) */
  sources: string[];
}

const SDK_TABS: SDKTab[] = [
  {
    id: "react",
    label: "React",
    sources: [
      "https://raw.githubusercontent.com/youversion/platform-sdk-react/refs/heads/main/packages/ui/CHANGELOG.md",
    ],
  },
  {
    id: "swift",
    label: "Swift",
    sources: [
      "https://raw.githubusercontent.com/youversion/platform-sdk-swift/refs/heads/main/CHANGELOG.md",
    ],
  },
  {
    id: "kotlin",
    label: "Kotlin",
    sources: [
      "https://raw.githubusercontent.com/youversion/platform-sdk-kotlin/refs/heads/main/CHANGELOG.md",
    ],
  },
  {
    id: "react-native",
    label: "React Native Expo",
    sources: [
      "https://raw.githubusercontent.com/youversion/platform-sdk-reactnative-expo/refs/heads/main/packages/ui/CHANGELOG.md",
      "https://raw.githubusercontent.com/youversion/platform-sdk-reactnative-expo/refs/heads/main/packages/core/CHANGELOG.md",
    ],
  },
  {
    id: "javascript",
    label: "JavaScript",
    sources: [
      "https://raw.githubusercontent.com/youversion/platform-sdk-react/refs/heads/main/packages/core/CHANGELOG.md",
    ],
  },
];

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-12 text-muted-foreground">
      <svg
        className="animate-spin -ml-1 mr-3 h-5 w-5"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      <span className="text-sm">Loading changelog…</span>
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="text-center py-12 text-muted-foreground">
      <p className="text-lg font-medium">Failed to load changelog</p>
      <p className="text-sm mt-2">
        There was a problem fetching the latest release notes.
      </p>
      <button
        onClick={onRetry}
        className="mt-4 px-4 py-2 text-sm font-medium rounded-md border border-border hover:bg-accent hover:text-accent-foreground transition-colors"
      >
        Try again
      </button>
    </div>
  );
}

/** Remove the first H1 heading (`# ...`) from markdown content */
function stripFirstH1(markdown: string): string {
  return markdown.replace(/^#\s+.+\n?/m, "");
}

/** Parse a changesets changelog into ordered `## version` sections */
function parseVersions(markdown: string): Map<string, string> {
  const sections = new Map<string, string>();
  const matches = [...markdown.matchAll(/^##\s+(\S+)\s*$/gm)];
  matches.forEach((match, i) => {
    const start = (match.index ?? 0) + match[0].length;
    const end = matches[i + 1]?.index ?? markdown.length;
    sections.set(match[1], markdown.slice(start, end).trim());
  });
  return sections;
}

/**
 * Merge changelogs from fixed-versioning monorepo packages into one document.
 * Sections with the same version are combined; blocks (headings and top-level
 * list items) that appear verbatim in more than one package are shown once,
 * and internal "Updated dependencies" entries are dropped.
 */
function mergeChangelogs(changelogs: string[]): string {
  if (changelogs.length === 1) return stripFirstH1(changelogs[0]);

  const parsed = changelogs.map((c) => parseVersions(stripFirstH1(c)));
  const versions: string[] = [];
  for (const sections of parsed) {
    for (const version of sections.keys()) {
      if (!versions.includes(version)) versions.push(version);
    }
  }

  return versions
    .map((version) => {
      const seen = new Set<string>();
      const blocks: string[] = [];
      for (const sections of parsed) {
        const body = sections.get(version);
        if (!body) continue;
        // Blocks = headings or top-level list items (with indented continuations)
        for (const block of body.split(/\n(?=\S)/)) {
          const key = block.trim();
          if (!key || seen.has(key)) continue;
          if (key.startsWith("- Updated dependencies")) continue;
          seen.add(key);
          blocks.push(key);
        }
      }
      // Drop headings left empty after dedupe (e.g. a lone "### Patch Changes")
      const kept = blocks.filter(
        (block, i) =>
          !block.startsWith("#") ||
          (blocks[i + 1] !== undefined && !blocks[i + 1].startsWith("#"))
      );
      return `## ${version}\n\n${kept.join("\n\n")}`;
    })
    .join("\n\n");
}

export function ChangelogPage() {
  const [activeTab, setActiveTab] = useState(SDK_TABS[0].id);
  const [fetchedContent, setFetchedContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);

  const cache = useRef<Map<string, string>>(new Map());

  const activeSDK = SDK_TABS.find((tab) => tab.id === activeTab)!;

  const fetchChangelog = useCallback(
    async (sdk: SDKTab, signal: AbortSignal) => {
      if (cache.current.has(sdk.id)) {
        setFetchedContent(cache.current.get(sdk.id)!);
        setError(false);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(false);

      try {
        const texts = await Promise.all(
          sdk.sources.map(async (url) => {
            const response = await fetch(url, { signal });
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            return response.text();
          })
        );
        const combined = mergeChangelogs(texts);
        cache.current.set(sdk.id, combined);
        setFetchedContent(combined);
      } catch (err) {
        if (signal.aborted) return;
        console.error(`Failed to fetch changelog for ${sdk.label}:`, err);
        setError(true);
        setFetchedContent(null);
      } finally {
        if (!signal.aborted) {
          setIsLoading(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    const controller = new AbortController();
    void fetchChangelog(activeSDK, controller.signal);
    return () => controller.abort();
  }, [activeSDK, fetchChangelog]);

  return (
    <div>
      <div className="flex gap-1 border-b border-border mb-6" role="tablist">
        {SDK_TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium transition-colors rounded-t-md ${
              activeTab === tab.id
                ? "border-b-2 border-primary text-primary"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState
          onRetry={() => {
            const c = new AbortController();
            void fetchChangelog(activeSDK, c.signal);
          }}
        />
      ) : fetchedContent ? (
        <Markdown content={fetchedContent} />
      ) : null}
    </div>
  );
}
